/**
 * Runtime error monitoring — Sentry when available, graceful fallback when not.
 * ---------------------------------------------------------------------------
 * The app ships without a hard Sentry dependency. If a Sentry SDK is present on
 * `window.Sentry` (loaded via a tag manager or a future integration), exceptions
 * are forwarded to it. Otherwise we degrade to a structured console report plus
 * an anonymous breadcrumb, so a widget crash is never silent and never blocks
 * the rest of the page.
 */

type SentryLike = {
  captureException?: (error: unknown, context?: unknown) => void;
  captureMessage?: (message: string, context?: unknown) => void;
};

export type MonitoringScope = {
  /** Stable surface id, e.g. "swap_widget" | "rpc_indicator" | "receipt_modal". */
  surface: string;
  [key: string]: unknown;
};

function getSentry(): SentryLike | undefined {
  if (typeof window === "undefined") return undefined;
  const s = (window as unknown as { Sentry?: SentryLike }).Sentry;
  return s && typeof s.captureException === "function" ? s : undefined;
}

export function isMonitoringEnabled(): boolean {
  return getSentry() !== undefined;
}

/** Report a runtime exception. Never throws. */
export function captureError(error: unknown, scope: MonitoringScope): void {
  try {
    const sentry = getSentry();
    if (sentry?.captureException) {
      sentry.captureException(error, { tags: { surface: scope.surface }, extra: scope });
      return;
    }
    // Graceful fallback — structured, PII-free console report.
    const message = error instanceof Error ? error.message : String(error);
    console.error(`[monitoring:${scope.surface}]`, message, {
      ...scope,
      stack: error instanceof Error ? error.stack : undefined,
    });
  } catch {
    /* monitoring must never break the app */
  }
}

/** Report a non-exception anomaly (degraded RPC, failed download, …). */
export function captureWarning(message: string, scope: MonitoringScope): void {
  try {
    const sentry = getSentry();
    if (sentry?.captureMessage) {
      sentry.captureMessage(message, { level: "warning", tags: { surface: scope.surface }, extra: scope });
      return;
    }
    console.warn(`[monitoring:${scope.surface}]`, message, scope);
  } catch {
    /* noop */
  }
}

/* ────────────────────────────────────────────────────────────────── */
/* Global runtime monitoring (production diagnostics)                 */
/* ────────────────────────────────────────────────────────────────── */

let monitoringInitialized = false;

/**
 * Install browser-level handlers so uncaught exceptions and rejected
 * promises are reported through the same pipeline as widget boundaries.
 * Production bundles ship source maps, so stack frames resolve to the
 * original TypeScript in whichever collector consumes `window.Sentry`.
 * Safe to call multiple times; no-ops on the server.
 */
export function initMonitoring(): void {
  if (monitoringInitialized || typeof window === "undefined") return;
  monitoringInitialized = true;
  try {
    window.addEventListener("error", (event) => {
      captureError(event.error ?? event.message, {
        surface: "window_error",
        source: (event as ErrorEvent).filename,
        line: (event as ErrorEvent).lineno,
        column: (event as ErrorEvent).colno,
      });
    });
    window.addEventListener("unhandledrejection", (event) => {
      captureError((event as PromiseRejectionEvent).reason, {
        surface: "unhandled_rejection",
      });
    });
  } catch {
    /* monitoring must never break the app */
  }
}
